import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchOssDashboard } from "../../api/ossApi";
import { OssPageHeader } from "../../components/common/OssPageHeader";
import { OssDashboardKpiBoard } from "../../components/dashboard/OssDashboardKpiBoard";
import { OssDashboardTimelinePanel } from "../../components/dashboard/OssDashboardTimelinePanel";
import {
  initialDashboardData,
  kpiConfig,
} from "../../components/dashboard/dashboardConfig";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";
import { OssLifecycleDialog } from "../../components/orders/OssLifecycleDialog";

export const OssDashboard = () => {
  const { group_code, project_code } = useParams();
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [stageFilter, setStageFilter] = useState("total");
  const [ganttFocus, setGanttFocus] = useState(0);
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);
  const { data, loading, error, load } = useOssAsyncData({
    fetcher: () => fetchOssDashboard(group_code, project_code),
    fallbackData: initialDashboardData,
    errorMessage: "운영 현황을 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });

  const lifecycle = error ? null : data?.lifecycle;
  const kpiItems = useMemo(
    () =>
      kpiConfig.map((item) => ({
        ...item,
        value: lifecycle ? Number(lifecycle[item.key] || 0).toLocaleString("ko-KR") : "—",
      })),
    [lifecycle]
  );

  if (loading && !data?.lifecycle) {
    return (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
        <CircularProgress size={34} />
      </Box>
    );
  }

  return (
    <Box
      className="oss-page oss-page--dashboard"
      sx={{
        height: { xs: "auto", lg: "100%" },
        minHeight: 0,
        bgcolor: "#f3f6fa",
        overflow: { xs: "auto", lg: "hidden" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing={{ xs: 2.5, lg: 1.65 }} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <OssPageHeader
          title="주문일정관리 현황"
          iconName="homeIcon"
        />

        {error && (
          <Alert severity="error" action={<Button color="inherit" onClick={load}>재시도</Button>}>
            {error}
          </Alert>
        )}

        {!error && !lifecycle && (
          <Alert severity="warning" action={<Button color="inherit" onClick={load}>다시 조회</Button>}>
            주문 단계별 집계를 불러올 수 없습니다. 서버에 최신 KPI API가 반영되었는지 확인해 주세요.
          </Alert>
        )}

        <OssDashboardKpiBoard items={kpiItems} activeKey={stageFilter} onItemClick={(key) => {
          setStageFilter(key);
          setGanttFocus((value) => value + 1);
        }} />

        <Box sx={{ flex: 1, minHeight: 0, height: "100%", maxHeight: "100%", display: "flex", overflow: "hidden" }}>
          <OssDashboardTimelinePanel onOpenOrder={setSelectedOrderId}
            lifecycleOrders={error ? [] : data?.lifecycleOrders}
            businessDate={data?.businessDate} stageFilter={stageFilter} ganttFocus={ganttFocus} lifecycleLoading={loading}
            refreshKey={scheduleRefreshKey} />
        </Box>
      </Stack>
      <OssLifecycleDialog open={Boolean(selectedOrderId)} orderId={selectedOrderId}
        groupCode={group_code} projectCode={project_code} title="주문 상세 및 조치"
        onClose={() => setSelectedOrderId("")} onSaved={() => {
          setScheduleRefreshKey((value) => value + 1);
          return load();
        }} />
    </Box>
  );
};
